// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import waitFor from "@insite/client-framework/Common/Utilities/waitFor";
import { createHandlerChainRunner, Handler } from "@insite/client-framework/HandlerCreator";
import { ApiParameter } from "@insite/client-framework/Services/ApiService";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import loadCustomRealTimeInventory from "@plumberssupply/App/Handlers/SystemBuilder/loadCustomRealTimeInventory";
import { System } from "@plumberssupply/App/Store/Reducers";

interface Parameter {
    systems: System[];
}

export interface GetPriceAndInventoryParameter extends ApiParameter {
    systems: System[];
}

interface Props {
    apiParameter: GetPriceAndInventoryParameter;
    product: ProductModel;
    productsInfo: ProductInfo[];
    pricingLoaded?: boolean;
    pids: string[];
    apiResult: ProductCollectionModel;
}

type HandlerType = Handler<Parameter, Props>;

export const PopulateApiParameter: HandlerType = (props: { apiParameter: any; parameter: any }) => {
    props.apiParameter = props.parameter;
};

export const DispatchBeginLoadProductsInfo: HandlerType = props => {
    props.dispatch({
        type: "xSystemBuilder/BeginLoadProductsInfo",
    } as any);

    // save all products ids of all systems
    const pids: string[] = [];
    const productsInfos: ProductInfo[] = [];

    props.parameter.systems?.forEach((system: System) => {
        pids.push(...system.productIds);
        system?.products?.forEach(product => {
            const info = {
                qtyOrdered: product.qty,
                productId: product.id,
                unitOfMeasure: product.unitOfMeasure,
                productDetailPath: "",
            };
            productsInfos.push(info);
        });
    });
    props.pids = pids;
    props.productsInfo = productsInfos;
};

export const LoadPricing: HandlerType = async props => {
    let pricingLoaded = false;
    props.dispatch(
        loadRealTimePricing({
            productPriceParameters: props.productsInfo,
            onSuccess: realTimePricing => {
                if (realTimePricing) {
                    props.productsInfo = props.productsInfo.map(item => {
                        const pricing = realTimePricing.realTimePricingResults?.find(
                            o => o.productId === item.productId,
                        );
                        return { ...item, pricing };
                    });
                }
                pricingLoaded = true;
            },
            onError: () => {
                pricingLoaded = true;
                // solving error thrown when failedToLoadPricing
                props.productsInfo = props.productsInfo.map(item => {
                    return { ...item, failedToLoadPricing: true };
                });
                SetProductInfo(props);
            },
            onComplete(realTimePricingProps) {
                if (realTimePricingProps.apiResult) {
                    this.onSuccess?.(realTimePricingProps.apiResult);
                } else if (realTimePricingProps.error) {
                    this.onError?.(realTimePricingProps.error);
                }
            },
        }),
    );
    await waitFor(() => pricingLoaded);
};

export const LoadInventory: HandlerType = async props => {
    const pids = props.pids;
    if (!pids || pids.length === 0) {
        return;
    }

    let loadedInventory = false;

    const qtys: { [key: string]: number } = {};
    props.productsInfo.forEach(item => {
        qtys[item.productId] = item.qtyOrdered;
    });

    props.dispatch(
        loadCustomRealTimeInventory({
            productIds: pids,
            qtys,
            onComplete: realTimeInventoryProps => {
                if (realTimeInventoryProps.error) {
                    if (props.productsInfo) {
                        props.productsInfo &&
                            props.productsInfo.forEach(item => {
                                item.failedToLoadInventory = true;
                            });
                    }
                } else if (props.productsInfo) {
                    props.productsInfo = props.productsInfo.map(item => {
                        const inventory = realTimeInventoryProps.apiResult?.realTimeInventoryResults?.find(
                            o => o.productId === item.productId,
                        );
                        return { ...item, inventory };
                    });
                }
                loadedInventory = true;
            },
        }),
    );

    await waitFor(() => loadedInventory);
};

export const SetProductInfo: HandlerType = props => {
    if (props.productsInfo) {
        let productsInfo = {};
        props.productsInfo.forEach(item => {
            const productId = (item as any).productId;
            productsInfo = {
                [productId]: item,
                ...productsInfo,
            };
        });

        props.dispatch({
            type: "xSystemBuilder/CompleteLoadProductsInfo",
            productsInfo,
        } as any);
    }
};

export const chain = [PopulateApiParameter, DispatchBeginLoadProductsInfo, LoadPricing, LoadInventory, SetProductInfo];

const getSystemsPriceAndInventory = createHandlerChainRunner(chain, "GetSystemsPriceAndInventory");
export default getSystemsPriceAndInventory;
